import { supabase } from "@/integrations/supabase/client";

interface RouteTrackingData {
  guestUserId: string;
  hostUserId: string;
  addressShareId?: string;
  currentLatitude: number;
  currentLongitude: number;
  destinationLatitude: number;
  destinationLongitude: number;
  destinationAddress: string;
  totalDistanceKm?: number;
  estimatedDurationMinutes?: number;
}

interface RouteTracking {
  id: string;
  guest_user_id: string;
  host_user_id: string;
  address_share_id: string | null;
  current_latitude: number;
  current_longitude: number;
  destination_latitude: number;
  destination_longitude: number;
  destination_address: string;
  total_distance_km: number | null;
  estimated_duration_minutes: number | null;
  current_eta: string | null;
  status: string;
  is_active: boolean;
  started_at: string;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

interface LocationUpdate {
  id: string;
  route_tracking_id: string;
  latitude: number;
  longitude: number;
  accuracy: number | null;
  heading: number | null;
  speed: number | null;
  created_at: string;
}

export const routeTrackingService = {
  // Start route tracking
  async startRouteTracking(data: RouteTrackingData): Promise<RouteTracking> {
    const { data: routeData, error } = await supabase
      .from('route_tracking')
      .insert({
        guest_user_id: data.guestUserId,
        host_user_id: data.hostUserId,
        address_share_id: data.addressShareId || null,
        current_latitude: data.currentLatitude,
        current_longitude: data.currentLongitude,
        destination_latitude: data.destinationLatitude,
        destination_longitude: data.destinationLongitude,
        destination_address: data.destinationAddress,
        total_distance_km: data.totalDistanceKm,
        estimated_duration_minutes: data.estimatedDurationMinutes,
        status: 'active',
        is_active: true,
      })
      .select()
      .single();

    if (error) throw error;
    return routeData;
  },

  // Update current location during route tracking
  async updateLocation(routeTrackingId: string, latitude: number, longitude: number, metadata?: {
    accuracy?: number;
    heading?: number;
    speed?: number;
  }): Promise<void> {
    // Insert location update record
    const { error: updateError } = await supabase
      .from('route_tracking_updates')
      .insert({
        route_tracking_id: routeTrackingId,
        latitude,
        longitude,
        accuracy: metadata?.accuracy,
        heading: metadata?.heading,
        speed: metadata?.speed,
      });

    if (updateError) throw updateError;

    // Update current position in route_tracking table
    const { error: routeError } = await supabase
      .from('route_tracking')
      .update({
        current_latitude: latitude,
        current_longitude: longitude,
      })
      .eq('id', routeTrackingId);

    if (routeError) throw routeError;
  },

  // Complete route tracking
  async completeRouteTracking(routeTrackingId: string): Promise<void> {
    const { error } = await supabase
      .from('route_tracking')
      .update({
        status: 'completed',
        is_active: false,
        completed_at: new Date().toISOString(),
      })
      .eq('id', routeTrackingId);

    if (error) throw error;
  },

  // Cancel route tracking
  async cancelRouteTracking(routeTrackingId: string): Promise<void> {
    const { error } = await supabase
      .from('route_tracking')
      .update({
        status: 'cancelled',
        is_active: false,
        completed_at: new Date().toISOString(),
      })
      .eq('id', routeTrackingId);

    if (error) throw error;
  },

  // Get active route tracking for a user (as guest)
  async getActiveRouteTracking(userId: string): Promise<RouteTracking | null> {
    const { data, error } = await supabase
      .from('route_tracking')
      .select('*')
      .eq('guest_user_id', userId)
      .eq('is_active', true)
      .eq('status', 'active')
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return data;
  },

  // Get route tracking by ID
  async getRouteTracking(routeTrackingId: string): Promise<RouteTracking | null> {
    const { data, error } = await supabase
      .from('route_tracking')
      .select('*')
      .eq('id', routeTrackingId)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return data;
  },

  // Get route tracking where user is the host (to view guest's progress)
  async getRouteTrackingAsHost(hostUserId: string): Promise<RouteTracking[]> {
    const { data, error } = await supabase
      .from('route_tracking')
      .select('*')
      .eq('host_user_id', hostUserId)
      .eq('is_active', true)
      .eq('status', 'active')
      .order('started_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  // Calculate distance between two points (Haversine formula)
  calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * (Math.PI / 180);
    const dLng = (lng2 - lng1) * (Math.PI / 180);
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  },

  // Estimate arrival time based on current location and destination
  calculateETA(currentLat: number, currentLng: number, destLat: number, destLng: number, averageSpeedKmh: number = 5): Date {
    const distance = this.calculateDistance(currentLat, currentLng, destLat, destLng);
    const timeHours = distance / averageSpeedKmh;
    const eta = new Date();
    eta.setTime(eta.getTime() + (timeHours * 60 * 60 * 1000));
    return eta;
  },

  // Subscribe to real-time updates for route tracking
  subscribeToRouteUpdates(routeTrackingId: string, callback: (payload: any) => void) {
    return supabase
      .channel(`route-tracking-${routeTrackingId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'route_tracking',
          filter: `id=eq.${routeTrackingId}`,
        },
        callback
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'route_tracking_updates',
          filter: `route_tracking_id=eq.${routeTrackingId}`,
        },
        callback
      )
      .subscribe();
  },
};